import axios from "axios";
import type {
  Deal, DocumentMeta, DealAccessRecord, AccessLogEntry, ScenarioResponse,
  UploadResponse, ProjectionResult, MarketLeasingProfile, CamStructure,
  Comment, ActivityEntry, Notification,
} from "./types";

// Re-export all types so existing page imports from "../api/client" continue to work.
export type {
  AccessLogEntry, ActivityEntry, AnnualCashFlow, AuditFinding, AuditResult, AuditSummary,
  CamStructure, Comment, Deal, DealAccessRecord, Debt, DocumentMeta,
  ExpensePoolDefinition, LeaseUpAuditDetail, MarketLeasingProfile,
  MonthlyRentScheduleEntry, Notification, OpexBreakdown, OpexCategoryDetail, OperatingExpense,
  ParseFlag, ParseResult, ProjectionResult, PropertyData,
  ReimbursementBreakdown, ReimbursementDetail, ReturnMetrics,
  RolloverAssumptions, RolloverAuditDetail, Scenario, ScenarioAssumptions,
  ScenarioResponse, SpaceStatus, Tenant, TenantPGIDetail, UploadResponse,
} from "./types";


const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 120_000, // 2 minute timeout — prevents indefinite hangs
});

// Auth token provider — set by AuthContext or Layout
let _getToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenProvider(
  getToken: () => Promise<string | null>
): void {
  _getToken = getToken;
}

// Unauthorized handler — set by AuthContext to trigger React-level logout
// instead of a hard window.location redirect (which causes blank screens).
let _onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  _onUnauthorized = handler;
}

api.interceptors.request.use(async (config) => {
  if (_getToken) {
    const token = await _getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Extract backend error detail into error.message for easy consumption.
// On 401 (expired or invalid JWT), trigger logout via React state callback.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.detail) {
      error.message = error.response.data.detail;
    }
    if (error.response?.status === 401) {
      // Use the React-level logout handler if registered, which updates
      // auth state and lets ProtectedRoute redirect via React Router.
      // Falls back to hard redirect only if handler isn't registered yet.
      if (_onUnauthorized) {
        _onUnauthorized();
      } else {
        localStorage.removeItem("autoval_token");
        localStorage.removeItem("autoval_user");
        window.location.href = "/sign-in";
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Decode a JWT payload without verifying the signature.
 * Returns null if the token is malformed.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired (with 60-second buffer).
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec + 60; // 60-second buffer
}

// --- Deals ---













// --- Documents ---







// --- Scenarios (API response) ---









// --- Audit data interfaces ---

export interface TenantMonthlyAudit {
  tenant_id: string;
  tenant_name: string;
  suite_id: string | null;
  year: number;
  month: number;
  calendar_year: number;
  period_start: string;
  period_end: string;
  has_lease: boolean;
  occupied: boolean;
  in_free_rent: boolean;
  in_downtime: boolean;
  absorption_fraction: number;
  leased_sf: number;
  contract_rent_psf: number;
  rent_step_description: string | null;
  potential_rent: number;
  collected_rent: number;
  collection_explanation: string;
  annual_reimbursement: number;
  monthly_reimbursement: number;
  reimbursement_explanation: string;
  ti_this_month: number;
  lc_this_month: number;
  ti_lc_explanation: string | null;
  events_this_month: string[];
}

export interface ExpenseLineAudit {
  name: string;
  base_year_amount: number;
  growth_rate_applied: number;
  years_of_growth: number;
  current_year_amount: number;
  formula: string;
}

export interface YearlyExpenseAudit {
  year: number;
  calendar_year: number;
  base_year_label: string;
  expense_growth_rate: number;
  tax_growth_rate: number | null;
  insurance_growth_rate: number | null;
  lines: ExpenseLineAudit[];
  total_above_line: number;
  total_operating_expenses: number;
}

export interface ReimbursementPoolAudit {
  tenant_id: string;
  tenant_name: string;
  year: number;
  calendar_year: number;
  cam_type: string;
  leased_sf: number;
  total_leasable_area: number;
  explicit_pro_rata: number | null;
  computed_pro_rata: number;
  cam_pool_type: string;
  variable_expenses: number;
  fixed_expenses: number;
  occupancy_rate: number;
  gross_up_threshold: number;
  grossed_up_variable: number;
  exclusions_applied: string[];
  management_fee_load_pct: number;
  cam_pool_before_load: number;
  cam_pool_after_load: number;
  base_year: number | null;
  base_year_cam_pool: number | null;
  base_year_taxes: number | null;
  base_year_insurance: number | null;
  cam_reimbursement: number;
  cam_formula: string;
  current_taxes: number;
  tax_reimbursement: number;
  tax_formula: string;
  current_insurance: number;
  insurance_reimbursement: number;
  insurance_formula: string;
  admin_fee_pct: number;
  admin_fee_amount: number;
  total_reimbursement: number;
  total_formula: string;
  cap_pct: number | null;
  compounding: boolean | null;
  capped_amount: number | null;
  cap_formula: string | null;
}

export interface MonthlyOccupancyAudit {
  year: number;
  month: number;
  calendar_year: number;
  total_leasable_sf: number;
  occupied_sf: number;
  occupancy_rate: number;
  tenants_occupied: string[];
  tenants_vacant: string[];
}

export interface ReturnMetricsAudit {
  final_year_egi: number;
  final_year_opex: number;
  final_year_noi: number;
  revenue_growth_rate: number;
  expense_growth_rate: number;
  year_n_plus_1_egi: number;
  year_n_plus_1_opex: number;
  year_n_plus_1_noi: number;
  exit_cap_rate: number;
  gross_exit_price: number;
  selling_costs_pct: number;
  selling_costs: number;
  net_exit_proceeds: number;
  purchase_price: number;
  loan_amount: number;
  equity: number;
  unlevered_cf_stream: number[];
  levered_cf_stream: number[];
  going_in_cap: number | null;
  irr_unlevered: number | null;
  irr_levered: number | null;
  npv: number | null;
  equity_multiple: number | null;
  equity_multiple_unlevered: number | null;
  avg_cash_on_cash: number | null;
  avg_cash_on_cash_unlevered: number | null;
  avg_dscr: number | null;
  min_dscr: number | null;
  annual_dscr: number[];
  annual_coc: number[];
  annual_coc_unlevered: number[];
}

export interface EventTimelineEntry {
  date: string;
  tenant_id: string;
  tenant_name: string;
  event_type: string;
  description: string;
}

// --- API functions ---

export async function fetchDeals(): Promise<Deal[]> {
  const { data } = await api.get<Deal[]>("/api/deals");
  return data;
}

export async function fetchDeal(id: string): Promise<Deal> {
  const { data } = await api.get<Deal>(`/api/deals/${id}`);
  return data;
}

export async function createDeal(payload: {
  name: string;
  address?: string;
  asset_class?: string;
}): Promise<Deal> {
  const { data } = await api.post<Deal>("/api/deals", payload);
  return data;
}

export async function updateDeal(
  id: string,
  payload: Record<string, unknown>
): Promise<Deal> {
  const { data } = await api.put<Deal>(`/api/deals/${id}`, payload);
  return data;
}

export async function deleteDeal(id: string): Promise<void> {
  await api.delete(`/api/deals/${id}`);
}

export async function resolveConflict(
  dealId: string,
  body: { conflict_type: string; pool_id?: string; choice: string; tenant_name?: string },
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/api/deals/${dealId}/resolve-conflict`, body);
  return data;
}

export async function duplicateDeal(dealId: string): Promise<Deal> {
  const resp = await api.post(`/api/deals/${dealId}/duplicate`);
  return resp.data;
}


export async function uploadDocument(
  dealId: string,
  file: File,
  fileType?: string
): Promise<UploadResponse> {
  const form = new FormData();
  form.append("file", file);
  if (fileType) {
    form.append("file_type", fileType);
  }
  const { data } = await api.post<UploadResponse>(
    `/api/deals/${dealId}/documents`,
    form,
    { headers: { "Content-Type": undefined } }
  );
  return data;
}

export async function deleteDocument(
  dealId: string,
  docId: string
): Promise<void> {
  await api.delete(`/api/deals/${dealId}/documents/${docId}`);
}

export async function parseDocument(
  dealId: string,
  docId: string
): Promise<DocumentMeta> {
  const { data } = await api.post<DocumentMeta>(
    `/api/deals/${dealId}/documents/${docId}/parse`,
    undefined,
    { timeout: 300_000 } // 5 min — parse may run Mistral OCR + Claude AI enhancement
  );
  return data;
}

export async function enhanceDocument(
  dealId: string,
  docId: string
): Promise<DocumentMeta> {
  const { data } = await api.post<DocumentMeta>(
    `/api/deals/${dealId}/documents/${docId}/enhance`,
    undefined,
    { timeout: 300_000 } // 5 min — AI enhancement calls Claude API
  );
  return data;
}

export async function fetchDocuments(dealId: string): Promise<DocumentMeta[]> {
  const { data } = await api.get<DocumentMeta[]>(
    `/api/deals/${dealId}/documents`
  );
  return data;
}

export async function fetchDocument(
  dealId: string,
  docId: string
): Promise<DocumentMeta> {
  const { data } = await api.get<DocumentMeta>(
    `/api/deals/${dealId}/documents/${docId}`
  );
  return data;
}

export async function reclassifyDocument(dealId: string, docId: string, fileType: string): Promise<DocumentMeta> {
  const { data } = await api.patch<DocumentMeta>(`/api/deals/${dealId}/documents/${docId}/reclassify`, { file_type: fileType });
  return data;
}

export async function confirmDocument(dealId: string, docId: string): Promise<{ status: string; confirmed_at: string }> {
  const { data } = await api.post(`/api/deals/${dealId}/documents/${docId}/confirm`);
  return data;
}

export async function unconfirmDocument(dealId: string, docId: string): Promise<{ status: string }> {
  const { data } = await api.post(`/api/deals/${dealId}/documents/${docId}/unconfirm`);
  return data;
}

// ---------------------------------------------------------------------------
// Source Audit
// ---------------------------------------------------------------------------

export interface SourceAuditField {
  label: string;
  value: unknown;
  confidence: number;
}

export interface SourceAuditCamDetail {
  cam_type: string | null;
  confidence: number;
  [key: string]: unknown;
}

export interface SourceAuditTenant {
  tenant_name: string;
  fields: Record<string, SourceAuditField>;
  cam_detail: SourceAuditCamDetail | null;
}

export interface SourceAuditOpexYear {
  year: number | null;
  period_label: string;
  total_revenue: number;
  total_expenses: number;
  noi: number;
  expense_items: Array<{ label: string; category: string; expense_group?: string; amount: number }>;
  revenue_items: Array<{ label: string; category: string; amount: number }>;
}

export interface SourceAuditCamRecTenant {
  tenant_name: string;
  suite_id: string | null;
  leased_sf: number;
  pro_rata_share: number;
  lease_class: string;
  cam_estimated: number;
  cam_actual: number;
  cam_true_up: number;
  tax_estimated: number;
  tax_actual: number;
  tax_true_up: number;
  insurance_estimated: number;
  insurance_actual: number;
  insurance_true_up: number;
  total_estimated: number;
  total_actual: number;
  total_true_up: number;
  base_year: number | null;
  base_year_amount: number | null;
  stop_amount_psf: number;
  expense_cap_pct: number | null;
  admin_fee_pct: number;
  has_exclusions: boolean;
  exclusion_categories: string[];
}

export interface SourceAuditCamRecPool {
  pool_type: string;
  pool_name: string;
  total_amount: number;
  budgeted_amount: number;
  variance: number;
  admin_fee_pct: number;
  line_items: Array<{ name: string; amount: number }>;
}

export interface SourceAuditCamRec {
  reconciliation_year: number | null;
  total_leasable_area: number;
  gross_up_factor: number;
  expense_pools: SourceAuditCamRecPool[];
  tenants: SourceAuditCamRecTenant[];
}

export interface SourceAuditDocument {
  id: string;
  filename: string;
  file_type: string;
  parse_status: string;
  uploaded_at: string | null;
  confidence_score: number | null;
  tenants: SourceAuditTenant[];
  opex_years: SourceAuditOpexYear[];
  cam_rec: SourceAuditCamRec | null;
}

export async function fetchSourceAudit(dealId: string): Promise<SourceAuditDocument[]> {
  const { data } = await api.get<SourceAuditDocument[]>(`/api/deals/${dealId}/source-audit`);
  return data;
}

export async function fetchSourcePageBlob(
  dealId: string, docId: string, pageNum: number, highlight?: string,
): Promise<Blob> {
  const params: Record<string, string> = {};
  if (highlight) params.highlight = highlight;
  const resp = await api.get(`/api/deals/${dealId}/source-audit/${docId}/page/${pageNum}`, {
    responseType: "blob",
    timeout: 30_000,
    params,
  });
  return resp.data;
}

export async function findTenantSource(
  dealId: string, docId: string, tenantName: string,
): Promise<{ source_type: string; pages?: number[]; total_pages?: number; matches?: Array<{ sheet: string; row: number }>; found: boolean }> {
  const { data } = await api.get(`/api/deals/${dealId}/source-audit/${docId}/find-source`, {
    params: { tenant_name: tenantName },
  });
  return data;
}

export async function fetchPageCount(dealId: string, docId: string): Promise<number> {
  const { data } = await api.get<{ page_count: number }>(`/api/deals/${dealId}/source-audit/${docId}/page-count`);
  return data.page_count;
}

export async function fetchScenarios(
  dealId: string
): Promise<ScenarioResponse[]> {
  const { data } = await api.get<ScenarioResponse[]>(
    `/api/deals/${dealId}/scenarios`
  );
  return data;
}

export async function fetchScenarioDefaults(
  dealId: string
): Promise<{ asset_class: string; assumptions: Record<string, number> }> {
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/defaults`
  );
  return data;
}

export async function createScenario(
  dealId: string,
  payload: { name: string; assumptions?: Record<string, unknown> }
): Promise<ScenarioResponse> {
  const { data } = await api.post<ScenarioResponse>(
    `/api/deals/${dealId}/scenarios`,
    payload
  );
  return data;
}

export async function updateScenario(
  dealId: string,
  scenarioId: string,
  payload: { name?: string; assumptions?: Record<string, unknown> }
): Promise<ScenarioResponse> {
  const { data } = await api.put<ScenarioResponse>(
    `/api/deals/${dealId}/scenarios/${scenarioId}`,
    payload
  );
  return data;
}

export async function deleteScenario(
  dealId: string,
  scenarioId: string
): Promise<void> {
  await api.delete(`/api/deals/${dealId}/scenarios/${scenarioId}`);
}

export async function runScenario(
  dealId: string,
  scenarioId: string
): Promise<{ scenario_id: string; status: string; projection_result: ProjectionResult }> {
  const { data } = await api.post(
    `/api/deals/${dealId}/scenarios/${scenarioId}/run`
  );
  return data;
}

// --- Admin / Access Management ---



export async function fetchDealAccess(
  dealId: string
): Promise<DealAccessRecord[]> {
  const { data } = await api.get<DealAccessRecord[]>(
    `/api/deals/${dealId}/admin/access`
  );
  return data;
}

export async function grantAccess(
  dealId: string,
  payload: { email: string; role?: string }
): Promise<DealAccessRecord> {
  const { data } = await api.post<DealAccessRecord>(
    `/api/deals/${dealId}/admin/access`,
    payload
  );
  return data;
}

export async function revokeAccess(
  dealId: string,
  accessId: string
): Promise<void> {
  await api.delete(`/api/deals/${dealId}/admin/access/${accessId}`);
}

export async function fetchAccessLogs(
  dealId: string
): Promise<AccessLogEntry[]> {
  const { data } = await api.get<AccessLogEntry[]>(
    `/api/deals/${dealId}/admin/logs`
  );
  return data;
}

// --- Tenant CRUD ---

export async function updateTenant(
  dealId: string,
  tenantIndex: number,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const { data } = await api.patch(
    `/api/deals/${dealId}/tenants/${tenantIndex}`,
    payload
  );
  return data;
}

export async function addTenant(
  dealId: string,
  payload: {
    tenant_name: string;
    square_footage: number;
    base_rent_annual: number;
    suite_id?: string;
    lease_type?: string;
    lease_start?: string;
    lease_end?: string;
    rent_psf?: number;
    notes?: string;
  }
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/api/deals/${dealId}/tenants`, payload);
  return data;
}

export async function deleteTenant(
  dealId: string,
  tenantIndex: number
): Promise<void> {
  await api.delete(`/api/deals/${dealId}/tenants/${tenantIndex}`);
}

// --- Expense Overrides ---

export async function updateExpenseOverrides(
  dealId: string,
  overrides: Array<{
    category: string;
    action: string;
    value?: number;
    line_item?: string;
    note?: string;
  }>
): Promise<Record<string, unknown>> {
  const { data } = await api.patch(`/api/deals/${dealId}/expense-overrides`, {
    overrides,
  });
  return data;
}

// --- Deal Settings ---

export async function updateDealSettings(
  dealId: string,
  payload: Record<string, unknown>
): Promise<Deal> {
  const { data } = await api.put<Deal>(`/api/deals/${dealId}`, payload);
  return data;
}

// --- Tenant Reimbursement ---

export async function updateTenantReimbursement(
  dealId: string,
  tenantId: string,
  payload: {
    lease_type?: string;
    cam_structure?: Partial<CamStructure>;
    pro_rata_share?: number | null;
    pool_reimbursements?: Array<Record<string, unknown>>;
  }
): Promise<Record<string, unknown>> {
  const { data } = await api.patch(
    `/api/deals/${dealId}/tenants/${tenantId}/reimbursement`,
    payload
  );
  return data;
}

// --- Validate parse (learning mechanism) ---

export async function validateParse(
  dealId: string,
  docId: string
): Promise<{ id: string; status: string; file_type: string }> {
  const { data } = await api.post(
    `/api/deals/${dealId}/documents/${docId}/validate`
  );
  return data;
}

// --- Auto-project ---

export async function autoProject(
  dealId: string
): Promise<{
  scenario_id: string;
  scenario_name: string;
  status: string;
  projection_result?: ProjectionResult;
  error?: string;
}> {
  const { data } = await api.post(`/api/deals/${dealId}/auto-project`);
  return data;
}

// --- Deal-level assumptions + projection ---

export async function updateDealAssumptions(
  dealId: string,
  assumptions: Record<string, unknown>
): Promise<{ property_data: Record<string, unknown>; projection: Record<string, unknown> | null }> {
  const { data } = await api.patch(`/api/deals/${dealId}/assumptions`, assumptions);
  return data;
}

export async function projectDeal(
  dealId: string
): Promise<{ scenario_id: string; status: string; projection_result: ProjectionResult }> {
  const { data } = await api.post(`/api/deals/${dealId}/project`);
  return data;
}

// --- Update + Run Scenario (single round-trip) ---

export async function updateAndRunScenario(
  dealId: string,
  scenarioId: string,
  payload: { name?: string; assumptions?: Record<string, unknown> }
): Promise<{ scenario_id: string; status: string; projection_result: ProjectionResult }> {
  const { data } = await api.put(
    `/api/deals/${dealId}/scenarios/${scenarioId}/update-and-run`,
    payload
  );
  return data;
}

// --- Export ---

export async function downloadArgusExport(
  dealId: string,
  scenarioId: string
): Promise<void> {
  const response = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/export/argus`,
    { responseType: "blob" }
  );
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="(.+)"/);
  a.download = match ? match[1] : "Excel Export.xlsx";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadBovExport(
  dealId: string,
  scenarioId: string
): Promise<void> {
  const response = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/export/bov`,
    { responseType: "blob" }
  );
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="(.+)"/);
  a.download = match ? match[1] : "BOV Export.xlsx";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadModelExport(
  dealId: string,
  scenarioId: string
): Promise<void> {
  const response = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/export/model`,
    { responseType: "blob" }
  );
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="(.+)"/);
  a.download = match ? match[1] : "AutoVal Model.xlsx";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadProformaExport(
  dealId: string,
  scenarioId: string
): Promise<void> {
  const resp = await api.get(`/api/deals/${dealId}/export-proforma`, {
    params: { scenario_id: scenarioId },
    responseType: "blob",
  });
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  const cd = resp.headers["content-disposition"] || "";
  const match = cd.match(/filename="?(.+?)"?$/);
  a.download = match ? match[1] : "Pro Forma.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Auth: password change ---

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  const { data } = await api.put("/api/auth/password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

// --- Audit API ---

export async function fetchTenantMonthlyAudit(
  dealId: string,
  scenarioId: string,
  tenantId?: string,
  year?: number
): Promise<TenantMonthlyAudit[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  if (year) params.set('year', year.toString());
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/tenant-monthly?${params}`
  );
  return data;
}

export async function fetchExpenseAudit(
  dealId: string,
  scenarioId: string
): Promise<YearlyExpenseAudit[]> {
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/expenses`
  );
  return data;
}

export async function fetchReimbursementAudit(
  dealId: string,
  scenarioId: string,
  tenantId?: string,
  year?: number
): Promise<ReimbursementPoolAudit[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  if (year) params.set('year', year.toString());
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/reimbursements?${params}`
  );
  return data;
}

export async function fetchOccupancyAudit(
  dealId: string,
  scenarioId: string
): Promise<MonthlyOccupancyAudit[]> {
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/occupancy`
  );
  return data;
}

export async function fetchReturnsAudit(
  dealId: string,
  scenarioId: string
): Promise<ReturnMetricsAudit> {
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/returns`
  );
  return data;
}

export async function fetchEventTimeline(
  dealId: string,
  scenarioId: string,
  tenantId?: string
): Promise<EventTimelineEntry[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  const { data } = await api.get(
    `/api/deals/${dealId}/scenarios/${scenarioId}/audit/events?${params}`
  );
  return data;
}

// --- Detail Views ---

export async function fetchTenantDetail(
  dealId: string,
  tenantId: string,
  inputType: string
): Promise<Record<string, unknown>> {
  const { data } = await api.get(
    `/api/deals/${dealId}/tenants/${tenantId}/detail/${inputType}`
  );
  return data;
}

export async function saveTenantDetail(
  dealId: string,
  tenantId: string,
  inputType: string,
  entries: Record<string, unknown>[]
): Promise<Record<string, unknown>> {
  const { data } = await api.put(
    `/api/deals/${dealId}/tenants/${tenantId}/detail/${inputType}`,
    { entries }
  );
  return data;
}

export async function resetTenantDetail(
  dealId: string,
  tenantId: string,
  inputType: string
): Promise<Record<string, unknown>> {
  const { data } = await api.post(
    `/api/deals/${dealId}/tenants/${tenantId}/detail/${inputType}/reset`
  );
  return data;
}

export async function fetchExpenseDetail(
  dealId: string,
  category: string
): Promise<Record<string, unknown>> {
  const { data } = await api.get(
    `/api/deals/${dealId}/expenses/${category}/detail`
  );
  return data;
}

export async function saveExpenseDetail(
  dealId: string,
  category: string,
  entries: Record<string, unknown>[]
): Promise<Record<string, unknown>> {
  const { data } = await api.put(
    `/api/deals/${dealId}/expenses/${category}/detail`,
    { entries }
  );
  return data;
}

export async function deleteExpenseLineItem(
  dealId: string,
  group: string,
  name: string
): Promise<void> {
  await api.delete(
    `/api/deals/${dealId}/operating-expenses/line-item`,
    { params: { group, name } }
  );
}

// ---------------------------------------------------------------------------
// PDF Report
// ---------------------------------------------------------------------------

export async function downloadPdfReport(dealId: string, scenarioId?: string): Promise<void> {
  const params = scenarioId ? `?scenario_id=${scenarioId}` : "";
  const response = await api.get(
    `/api/deals/${dealId}/report-pdf${params}`,
    { responseType: "blob" },
  );
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const cd = response.headers["content-disposition"] || "";
  const match = cd.match(/filename="?(.+?)"?$/);
  a.download = match ? match[1] : "Investment Summary.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Lease Abstracts
// ---------------------------------------------------------------------------

export async function downloadTenantAbstract(dealId: string, tenantIdx: number): Promise<Blob> {
  const { data } = await api.get(`/api/deals/${dealId}/tenants/${tenantIdx}/abstract-pdf`, { responseType: 'blob' });
  return data;
}

export async function downloadAllAbstracts(dealId: string): Promise<Blob> {
  const { data } = await api.get(`/api/deals/${dealId}/abstracts-pdf`, { responseType: 'blob' });
  return data;
}

// ---------------------------------------------------------------------------
// Sensitivity Analysis
// ---------------------------------------------------------------------------

export interface SensitivityRequest {
  row_variable: string;
  row_min: number;
  row_max: number;
  row_step: number;
  col_variable?: string;
  col_min?: number;
  col_max?: number;
  col_step?: number;
  output_metric: string;
}

export interface SensitivityResponse {
  row_variable: string;
  row_values: number[];
  col_variable?: string;
  col_values?: number[];
  results: (number | null)[][];
  current_row_value: number;
  current_col_value?: number;
}

export async function runSensitivity(
  dealId: string,
  scenarioId: string,
  body: SensitivityRequest,
): Promise<SensitivityResponse> {
  const { data } = await api.post(
    `/api/deals/${dealId}/scenarios/${scenarioId}/sensitivity`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// AutoVal Chat Interface
// ---------------------------------------------------------------------------

export interface ChatChange {
  field_name: string;
  new_value: unknown;
  old_value?: unknown;
  reason?: string;
  target?: string;
  tenant_name?: string;
  is_document_override?: boolean;
}

export interface ChatInterpretResponse {
  action: "execute" | "clarify" | "confirm" | "error";
  changes: ChatChange[];
  question?: string;
  summary?: string;
  message?: string;
  impact_summary?: string;
  projection_result?: ProjectionResult;
  scenario_id?: string;
  session_id?: string;
  is_admin?: boolean;
  source_reference?: { tenant_name?: string; field?: string } | null;
}

export interface ChatSessionResponse {
  session_id: string;
  messages: Array<{ role: string; content: string; timestamp?: string }>;
}

export async function chatGetLatestSession(dealId: string): Promise<ChatSessionResponse> {
  const { data } = await api.get<ChatSessionResponse>(`/api/deals/${dealId}/chat/session/latest`);
  return data;
}

export async function chatCreateSession(dealId: string): Promise<ChatSessionResponse> {
  const { data } = await api.post<ChatSessionResponse>(`/api/deals/${dealId}/chat/session`);
  return data;
}

export async function chatInterpret(
  dealId: string,
  message: string,
  scenarioId?: string,
  sessionId?: string,
  activeTab?: string,
): Promise<ChatInterpretResponse> {
  // Consumes the NDJSON stream from /api/deals/:id/chat. The backend emits a
  // "thinking" keepalive every 10s so Railway's ~60s idle-proxy timeout
  // doesn't drop compound-instruction calls (which routinely exceed 60s).
  // The final "complete" chunk carries the ChatResponse payload.
  const token = _getToken ? await _getToken() : null;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 180_000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/deals/${dealId}/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        message,
        scenario_id: scenarioId,
        session_id: sessionId,
        active_tab: activeTab,
        // Change wizard: never auto-apply — the backend returns changes as
        // action="confirm" with old values; we apply via chatExecute after
        // the user approves them in the confirmation modal.
        preview: true,
      }),
      signal: controller.signal,
    });
  } catch (err) {
    window.clearTimeout(timeoutId);
    throw err;
  }

  if (response.status === 401) {
    window.clearTimeout(timeoutId);
    if (_onUnauthorized) {
      _onUnauthorized();
    } else {
      localStorage.removeItem("autoval_token");
      localStorage.removeItem("autoval_user");
      window.location.href = "/sign-in";
    }
    throw new Error("Session expired. Please sign in again.");
  }

  if (!response.ok || !response.body) {
    window.clearTimeout(timeoutId);
    let detail = `Chat request failed (${response.status})`;
    try {
      const errBody = await response.json();
      if (errBody?.detail) detail = errBody.detail;
    } catch {
      // response body wasn't JSON — keep generic detail
    }
    throw new Error(detail);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult: ChatInterpretResponse | null = null;

  const consumeLine = (raw: string): void => {
    const line = raw.trim();
    if (!line) return;
    let chunk: Record<string, unknown>;
    try {
      chunk = JSON.parse(line);
    } catch {
      return; // skip malformed chunk
    }
    if (chunk.status === "complete") {
      delete chunk.status;
      finalResult = chunk as unknown as ChatInterpretResponse;
    }
    // "thinking" keepalives have no client-side effect.
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIdx = buffer.indexOf("\n");
      while (newlineIdx !== -1) {
        consumeLine(buffer.slice(0, newlineIdx));
        buffer = buffer.slice(newlineIdx + 1);
        newlineIdx = buffer.indexOf("\n");
      }
    }
    if (buffer) consumeLine(buffer);
  } finally {
    window.clearTimeout(timeoutId);
    try { reader.releaseLock(); } catch { /* already released */ }
  }

  if (!finalResult) {
    throw new Error("Chat response ended before a result was received.");
  }
  return finalResult;
}

export async function chatExecute(
  dealId: string,
  scenarioId: string,
  changes: ChatChange[],
  sessionId?: string,
): Promise<ChatInterpretResponse> {
  const { data } = await api.post<ChatInterpretResponse>(
    `/api/deals/${dealId}/chat/execute`,
    { scenario_id: scenarioId, changes, session_id: sessionId },
    { timeout: 60_000 },
  );
  return data;
}

export async function chatUndo(
  dealId: string,
  scenarioId: string,
  previousAssumptions: Record<string, unknown>,
  previousDealFields?: Record<string, unknown>,
): Promise<ChatInterpretResponse> {
  const { data } = await api.post<ChatInterpretResponse>(
    `/api/deals/${dealId}/chat/undo`,
    {
      scenario_id: scenarioId,
      previous_assumptions: previousAssumptions,
      previous_deal_fields: previousDealFields ?? {},
    },
    { timeout: 60_000 },
  );
  return data;
}

export async function createScenarioFromChat(
  dealId: string,
  sourceScenarioId: string,
  name: string,
  overrides?: Record<string, unknown>,
): Promise<ScenarioResponse> {
  const { data } = await api.post<ScenarioResponse>(
    `/api/deals/${dealId}/scenarios/from-chat`,
    {
      source_scenario_id: sourceScenarioId,
      name,
      assumption_overrides: overrides ?? {},
    },
  );
  return data;
}

export async function createStressTest(
  dealId: string,
  sourceScenarioId: string,
  variable: string,
): Promise<ScenarioResponse[]> {
  const { data } = await api.post<ScenarioResponse[]>(
    `/api/deals/${dealId}/scenarios/stress-test`,
    { source_scenario_id: sourceScenarioId, variable },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Expense Pools
// ---------------------------------------------------------------------------

export interface ExpensePoolItem {
  line_item_name: string;
  category: string;
  base_year_amount: number;
  current_year_amount: number;
  included: boolean;
  is_controllable?: boolean;
  is_variable?: boolean | null;
  [key: string]: unknown;
}

export interface ExpensePool {
  pool_id: string;
  pool_name: string;
  pool_type: string;
  items: ExpensePoolItem[];
  management_fee_included: boolean;
  management_fee_pct: number;
  management_fee_cap?: number | null;
  admin_fee_pct: number;
  total_amount: number;
  default_recovery_method: string;
  default_base_year: number | null;
  default_base_year_amount: number | null;
  default_expense_cap_pct: number | null;
  default_cap_compounding: boolean;
  default_cap_floor_pct?: number | null;
  cap_controllable_only?: boolean;
  gross_up_enabled: boolean;
  gross_up_threshold: number;
}

export async function updateExpensePool(
  dealId: string,
  poolId: string,
  payload: Partial<ExpensePool>,
): Promise<ExpensePool> {
  const { data } = await api.patch(
    `/api/deals/${dealId}/expense-pools/${poolId}`,
    payload,
  );
  return data;
}

export async function createExpensePool(
  dealId: string,
  payload: { pool_name: string; pool_type?: string; source_categories?: string[] },
): Promise<ExpensePool> {
  const { data } = await api.post(
    `/api/deals/${dealId}/expense-pools`,
    payload,
  );
  return data;
}

export async function deleteExpensePool(
  dealId: string,
  poolId: string,
): Promise<void> {
  await api.delete(`/api/deals/${dealId}/expense-pools/${poolId}`);
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export async function fetchComments(dealId: string, page?: string): Promise<Comment[]> {
  const params = page ? { page } : {};
  const { data } = await api.get<Comment[]>(`/api/deals/${dealId}/comments`, { params });
  return data;
}

export async function createComment(dealId: string, body: { data_path?: string; page?: string; content: string; parent_id?: string }): Promise<Comment> {
  const { data } = await api.post<Comment>(`/api/deals/${dealId}/comments`, body);
  return data;
}

export async function resolveComment(dealId: string, commentId: string): Promise<Comment> {
  const { data } = await api.patch<Comment>(`/api/deals/${dealId}/comments/${commentId}/resolve`);
  return data;
}

export async function deleteComment(dealId: string, commentId: string): Promise<void> {
  await api.delete(`/api/deals/${dealId}/comments/${commentId}`);
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export async function fetchActivity(dealId: string, limit = 50): Promise<ActivityEntry[]> {
  const { data } = await api.get<ActivityEntry[]>(`/api/deals/${dealId}/activity`, { params: { limit } });
  return data;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function fetchNotifications(unreadOnly = false): Promise<Notification[]> {
  const { data } = await api.get<Notification[]>("/api/notifications", { params: { unread_only: unreadOnly } });
  return data;
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await api.get<{ unread_count: number }>("/api/notifications/count");
  return data.unread_count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/api/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.post("/api/notifications/read-all");
}

// ---------------------------------------------------------------------------
// Deal Scoring
// ---------------------------------------------------------------------------

export interface ScoreFactor {
  name: string;
  score: number;
  weight: number;
  value: number | string;
  label: string;
  thresholds: string;
}

export interface DealScore {
  overall_score: number;
  letter_grade: string;
  factors: ScoreFactor[];
}

export async function fetchDealScore(dealId: string): Promise<DealScore> {
  const { data } = await api.get(`/api/deals/${dealId}/score`);
  return data;
}

// ---------------------------------------------------------------------------
// AI Lease Analysis
// ---------------------------------------------------------------------------

export interface LeaseAnalysisResponse {
  tenant_name: string;
  tenant_idx: number;
  analysis: {
    cam_recovery: Record<string, any>;
    tax_recovery: Record<string, any>;
    insurance_recovery: Record<string, any>;
    pro_rata_share: number | null;
    lease_type_summary: string | null;
    confidence: number;
    notes: string[];
  };
  suggested_update: {
    lease_type: string;
    cam_structure: Record<string, any>;
    pro_rata_share: number | null;
  };
}

export async function analyzeTenantLease(
  dealId: string,
  tenantIdx: number,
  file: File,
): Promise<LeaseAnalysisResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(
    `/api/deals/${dealId}/tenants/${tenantIdx}/analyze-lease`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Tax Lookup
// ---------------------------------------------------------------------------

export interface TaxAssessment {
  folio_number: string;
  county_code: number;
  county_name: string;
  just_value: number | null;
  assessed_value_nsd: number | null;
  assessed_value_sd: number | null;
  taxable_value_nsd: number | null;
  taxable_value_sd: number | null;
  land_value: number | null;
  building_value: number | null;
  millage_rate: number | null;
  estimated_annual_tax: number | null;
  homestead_value: number | null;
  has_exemptions: boolean;
  owner_name: string | null;
  physical_address: string | null;
  physical_city: string | null;
  physical_zip: string | null;
  use_code: string | null;
  year_built: number | null;
  building_area_sf: number | null;
  land_area_sf: number | null;
  tax_year: number | null;
  fetched_at: string | null;
  source: string | null;
}

export interface PostSaleTaxProjection {
  purchase_price: number;
  millage_rate: number;
  assessment_ratio: number;
  assessment_cap_pct: number | null;
  hold_period_years: number;
  year1_assessed_value: number;
  year1_estimated_tax: number;
  annual_projection: { year: number; assessed_value: number; tax: number }[];
}

export interface CountyInfo {
  code: number;
  name: string;
}

export async function fetchCounties(dealId: string): Promise<CountyInfo[]> {
  const { data } = await api.get(`/api/deals/${dealId}/tax/counties`);
  return data.counties;
}

export async function lookupTaxData(
  dealId: string,
  countyCode: number,
  folioNumbers: string[],
): Promise<{ assessments: TaxAssessment[]; not_found: string[]; errors: string[] }> {
  const { data } = await api.post(`/api/deals/${dealId}/tax/lookup`, {
    county_code: countyCode,
    folio_numbers: folioNumbers,
  });
  return data;
}

export async function refreshTaxData(
  dealId: string,
): Promise<{ assessments: TaxAssessment[]; not_found: string[]; errors: string[] }> {
  const { data } = await api.post(`/api/deals/${dealId}/tax/refresh`);
  return data;
}

export async function calculatePostSaleTax(
  dealId: string,
  purchasePrice: number,
  millageRate: number,
  assessmentRatio: number,
  assessmentCapPct: number | null,
  holdPeriodYears: number,
): Promise<PostSaleTaxProjection> {
  const { data } = await api.post(`/api/deals/${dealId}/tax/post-sale`, {
    purchase_price: purchasePrice,
    millage_rate: millageRate,
    assessment_ratio: assessmentRatio,
    assessment_cap_pct: assessmentCapPct,
    hold_period_years: holdPeriodYears,
  });
  return data;
}

export async function applyTaxToOS(
  dealId: string,
  source: "historical" | "post_sale",
  amount: number,
): Promise<{ status: string; category: string; amount: number; source: string }> {
  const { data } = await api.post(`/api/deals/${dealId}/tax/apply-to-os`, {
    source,
    amount,
  });
  return data;
}


// ---------------------------------------------------------------------------
// Verification & Corrections (Phase 3 — Correction Learning System)
// ---------------------------------------------------------------------------

export interface VerifiableField {
  field_path: string;
  label: string;
  value: unknown;
  is_critical: boolean;
  section: string;
  state: "unverified" | "verified" | "corrected" | "flag";
  original_value: unknown | null;
  confidence?: number | null;
}

export interface DocumentVerificationProgress {
  doc_id: string;
  filename: string;
  file_type: string;
  total_fields: number;
  verified_fields: number;
  critical_total: number;
  critical_verified: number;
  pct: number;
}

export interface VerificationStatus {
  deal_id: string;
  overall_verified: number;
  overall_total: number;
  overall_pct: number;
  critical_verified: number;
  critical_total: number;
  critical_pct: number;
  can_proceed: boolean;
  documents: DocumentVerificationProgress[];
}

export interface CorrectionRecord {
  id: string;
  document_id: string;
  field_path: string;
  original_value: unknown;
  corrected_value: unknown;
  correction_type: string;
  notes: string | null;
  corrected_at: string;
}

export async function fetchVerificationStatus(dealId: string): Promise<VerificationStatus> {
  const { data } = await api.get<VerificationStatus>(`/api/deals/${dealId}/verification-status`);
  return data;
}

export async function fetchDocumentFields(
  dealId: string, docId: string,
): Promise<{ doc_id: string; file_type: string; fields: VerifiableField[] }> {
  const { data } = await api.get(`/api/deals/${dealId}/documents/${docId}/fields`);
  return data;
}

export async function verifyField(
  dealId: string, docId: string, fieldPath: string,
): Promise<{ field_path: string; status: string }> {
  const { data } = await api.post(`/api/deals/${dealId}/documents/${docId}/verify-field`, {
    field_path: fieldPath,
  });
  return data;
}

export async function recordCorrection(
  dealId: string,
  docId: string,
  fieldPath: string,
  correctedValue: unknown,
  correctionType = "edit",
  notes?: string,
): Promise<CorrectionRecord> {
  const { data } = await api.post<CorrectionRecord>(
    `/api/deals/${dealId}/documents/${docId}/corrections`,
    { field_path: fieldPath, corrected_value: correctedValue, correction_type: correctionType, notes },
  );
  return data;
}

export async function verifyAllRemaining(
  dealId: string, docId: string,
): Promise<{ verified_count: number; status: string }> {
  const { data } = await api.post(`/api/deals/${dealId}/documents/${docId}/verify-all-remaining`);
  return data;
}

export async function fetchSourceSnippet(
  dealId: string, docId: string, fieldPath: string,
): Promise<{ field_path: string; snippet: string | null; doc_id: string }> {
  const { data } = await api.get(`/api/deals/${dealId}/documents/${docId}/source-snippet`, {
    params: { field_path: fieldPath },
  });
  return data;
}

export async function regenerateFieldSources(
  dealId: string, docId: string,
): Promise<{ doc_id: string; field_count: number; coverage: Record<string, unknown> }> {
  const { data } = await api.post(
    `/api/deals/${dealId}/documents/${docId}/regenerate-field-sources`,
    undefined,
    { timeout: 120_000 },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Parser Rules (Phase 4 — Rule Promotion)
// ---------------------------------------------------------------------------

export interface ParserRuleRecord {
  id: string;
  file_type: string;
  rule_type: string;
  match_criteria: Record<string, unknown>;
  action: Record<string, unknown>;
  source_correction_ids: string[] | null;
  approved_by_user_id: string;
  approved_at: string;
  hit_count: number;
  display_name: string;
}

export interface RuleSuggestion {
  field_path: string;
  correction_type: string;
  corrected_value: unknown;
  occurrence_count: number;
  document_count: number;
  structural_hashes: string[];
  source_correction_ids: string[];
  suggested_rule: Record<string, unknown>;
  description: string;
}

export async function fetchRules(): Promise<ParserRuleRecord[]> {
  const { data } = await api.get<ParserRuleRecord[]>("/api/rules");
  return data;
}

export async function createRule(payload: {
  file_type: string;
  rule_type: string;
  match_criteria: Record<string, unknown>;
  action: Record<string, unknown>;
  source_correction_ids?: string[];
}): Promise<ParserRuleRecord> {
  const { data } = await api.post<ParserRuleRecord>("/api/rules", payload);
  return data;
}

export async function deleteRule(ruleId: string): Promise<void> {
  await api.delete(`/api/rules/${ruleId}`);
}

export async function disableRule(ruleId: string): Promise<void> {
  await api.patch(`/api/rules/${ruleId}/disable`);
}

export async function fetchRuleSuggestions(
  dealId: string, docId: string,
): Promise<RuleSuggestion[]> {
  const { data } = await api.get<RuleSuggestion[]>(
    `/api/deals/${dealId}/documents/${docId}/rule-suggestions`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Audit Trail (Phase 5 — Provenance)
// ---------------------------------------------------------------------------

export interface AuditTrailField {
  field_path: string;
  label: string;
  section: string;
  is_critical: boolean;
  source_doc: string;
  source_doc_id: string;
  file_type: string;
  parser_output: unknown;
  final_value: unknown;
  status: string;
  verified_by: string | null;
  verified_at: string | null;
  correction_note: string | null;
  rule_applied: string | null;
}

export async function fetchAuditTrail(
  dealId: string,
  params?: { search?: string; status?: string; doc_id?: string },
): Promise<{ deal_id: string; total_fields: number; fields: AuditTrailField[] }> {
  const { data } = await api.get(`/api/deals/${dealId}/audit-trail`, { params });
  return data;
}

export async function downloadAuditExport(dealId: string): Promise<void> {
  const response = await api.post(
    `/api/deals/${dealId}/audit-export`,
    undefined,
    { responseType: "blob" },
  );
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const cd = response.headers["content-disposition"] || "";
  const match = cd.match(/filename="?(.+?)"?$/);
  a.download = match ? match[1] : "Audit Trail.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Property location (Google Maps)
// ---------------------------------------------------------------------------

export interface PropertyLocation {
  lat: number;
  lng: number;
  formatted_address: string | null;
  static_map_url: string | null;
  street_view_url: string | null;
  components: Record<string, string>;
}

export interface LocationStatus {
  configured: boolean;
  location: PropertyLocation | null;
}

export interface TenantSuggestion {
  name: string;
  types: string[];
  vicinity: string | null;
  place_id: string | null;
  business_status: string | null;
}

export async function fetchLocation(dealId: string): Promise<LocationStatus> {
  const { data } = await api.get(`/api/deals/${dealId}/location`);
  return data;
}

export async function lookupLocation(
  dealId: string,
  address: string,
): Promise<PropertyLocation> {
  const { data } = await api.post(`/api/deals/${dealId}/location/lookup`, { address });
  return data;
}

export async function lookupNearbyTenants(
  dealId: string,
): Promise<{ tenants: TenantSuggestion[]; lat: number; lng: number }> {
  const { data } = await api.get(`/api/deals/${dealId}/location/tenants`);
  return data;
}

// ---------------------------------------------------------------------------
// BOV / Offering Memorandum writeup (Claude API)
// ---------------------------------------------------------------------------

export interface BOVWriteup {
  markdown: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
}

export async function generateBOVWriteup(
  dealId: string,
  options?: { sections?: string[]; scenarioId?: string },
): Promise<BOVWriteup> {
  const { data } = await api.post(`/api/deals/${dealId}/bov-writeup`, {
    sections: options?.sections,
    scenario_id: options?.scenarioId,
  });
  return data;
}

export default api;
